import {
	Box,
	Icons,
	BoxProps,
	Tooltip,
	Button,
	Inline,
	Stack,
} from "@sembark-travel/ui-base"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import {
	Form,
	PhoneInputField,
	SubmissionError,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useRef, useState } from "react"
import type { IPhoneNumber } from "./store"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { useAuthUser, useHasFeatureFlag } from "../Auth"
import { useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

export function PhoneNumber({
	value,
	iconOnly,
	numberOnly,
	tooltipContainer,
	contactName,
	callLoggableId,
	callLoggableType,
	...props
}: Omit<BoxProps<"a">, "href" | "value"> & {
	value?: string | IPhoneNumber<unknown> | Array<IPhoneNumber<unknown>>
	iconOnly?: boolean
	numberOnly?: boolean
	tooltipContainer?: React.ComponentProps<typeof Tooltip>["container"]
	contactName?: string
	callLoggableType?:
		| "trips"
		| "trip_plan_requests"
		| "trip_sources"
		| "remote_call_logs"
	callLoggableId?: number | string
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [callingNumber, setCallingNumber] = useState<string | null>(null)
	const isLoading = callingNumber !== null
	const { user } = useAuthUser()
	const hasAccess = user?.has_active_telecalling_integration
	const canCall = useHasFeatureFlag("telecalling")
	const allowCall = Boolean(canCall) && callLoggableType && callLoggableId
	const disabledMessage =
		!hasAccess && canCall
			? "Add phone number in your account to initiate call."
			: undefined
	if (!value) return null

	let phoneNumbers: Array<string>
	if (Array.isArray(value)) {
		phoneNumbers = value.map((v) => v.phone_number)
	} else if (typeof value === "object") {
		phoneNumbers = [value.phone_number]
	} else {
		phoneNumbers = [value]
	}
	async function handleCall(e: React.MouseEvent, number: string) {
		e.preventDefault()
		e.stopPropagation()
		setCallingNumber(number)
		try {
			if (!callLoggableId || !callLoggableType) {
				throw new Error("Cannot initiate call: missing call context")
			}
			await xhr.post("/click-to-call", {
				loggable_type: callLoggableType,
				loggable_id: callLoggableId,
				customer_phone: number,
			})
			showSnackbar(`Calling ${contactName} on ${number}`)
		} catch (err) {
			showSnackbar(`Failed to initiate call: ${(err as Error).message}`)
		} finally {
			setCallingNumber(null)
			closeDialog()
		}
	}

	if (!phoneNumbers.length) return null

	return (
		<>
			<Dialog
				sm
				open={isOpen}
				onClose={isLoading ? undefined : closeDialog}
				title="Confirm Call"
			>
				<Dialog.Body>
					<Stack gap="2">
						<Box>
							Are you sure you want to call{" "}
							<Box fontWeight="semibold" as="span">
								{contactName}
							</Box>{" "}
							?
						</Box>

						{phoneNumbers.map((number, i) => (
							<Stack
								key={i}
								bgColor={"inset"}
								gap="2"
								paddingY="3"
								paddingX="4"
								borderRadius="md"
							>
								<Inline gap="2" justifyContent="between" alignItems="center">
									<Inline gap="1" alignItems="center">
										<Icons.Phone />
										<Box>{number}</Box>
									</Inline>
									<Button
										level="primary"
										type="button"
										size="sm"
										disabled={isLoading}
										onClick={(e) => handleCall(e, number)}
									>
										{callingNumber === number ? <Icons.Spinner spin /> : "Call"}
									</Button>
								</Inline>
							</Stack>
						))}
					</Stack>
				</Dialog.Body>
			</Dialog>
			<Tooltip
				interactive
				container={tooltipContainer}
				content={
					<Stack gap="2">
						{allowCall && Boolean(disabledMessage) ? (
							<Box color="warning" fontWeight="semibold">
								{disabledMessage}
							</Box>
						) : null}
						<Box as="ol">
							{phoneNumbers.map((value, i) => (
								<CopyToClipboard key={value}>
									{({ copyTextToClipboard }) => (
										<Box
											display="flex"
											alignItems="center"
											marginTop={i > 0 ? "2" : "0"}
											as="li"
										>
											<Box
												as="a"
												display="inlineBlock"
												color="on_emphasis"
												href={`tel:${value}`}
											>
												<Box marginRight="2" as="span" fontFamily="mono">
													{value}
												</Box>
											</Box>
											<CopyToClipboardButton
												inline
												iconOnly
												onClick={() => copyTextToClipboard(value)}
											>
												<Icons.Duplicate />
											</CopyToClipboardButton>
										</Box>
									)}
								</CopyToClipboard>
							))}
						</Box>
					</Stack>
				}
			>
				<Box display="inlineFlex" gap="1" alignItems="center">
					<Box
						as="a"
						href={`tel:${phoneNumbers[0]}`}
						display="inlineBlock"
						color="muted"
						{...props}
					>
						{!numberOnly && !allowCall ? <Icons.Phone opacity="50" /> : null}{" "}
						{!iconOnly ? phoneNumbers[0] : null}
					</Box>

					{allowCall ? (
						<Box>
							<Button
								size="sm"
								type="button"
								disabled={isLoading || Boolean(disabledMessage)}
								aria-label="Call customer"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									openDialog()
								}}
								level="secondary"
							>
								{isLoading ? (
									<Icons.Spinner animation="spin" />
								) : (
									<Icons.Phone />
								)}
							</Button>
						</Box>
					) : null}
					{phoneNumbers.length > 1 ? (
						<Box
							display="inlineBlock"
							marginLeft={!iconOnly ? "2" : "1"}
							color="muted"
							as="button"
							bgColor="transparent"
						>
							+{phoneNumbers.length - 1}
						</Box>
					) : null}
				</Box>
			</Tooltip>
		</>
	)
}

type TPhoneNumbersFormData = {
	phone_numbers: Array<{
		number?: string
		country_code: string
		is_primary: boolean
		phone_number: string
	}>
}

export function PhoneNumbersForm({
	phoneNumbers,
	onSubmit,
	onCancel,
}: {
	phoneNumbers?: Array<IPhoneNumber<unknown>>
	onSubmit: (data: {
		phone_numbers: Array<
			Omit<
				Required<TPhoneNumbersFormData["phone_numbers"][number]>,
				"phone_number"
			>
		>
	}) => Promise<unknown>
	onCancel?: () => void
}) {
	const initialValues: TPhoneNumbersFormData = useRef({
		phone_numbers: (phoneNumbers || []).map((p) => ({
			number: p.number,
			country_code: p.country_code,
			is_primary: p.is_primary,
			phone_number: p.phone_number,
		})),
	}).current
	return (
		<Form
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit({
					phone_numbers: (
						values.phone_numbers?.filter((p): p is Required<typeof p> =>
							Boolean(p.number)
						) || []
					).map((p) => ({
						country_code: p.country_code,
						number: p.number,
						is_primary: p.is_primary,
					})),
				})
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="4">
						<PhoneInputField
							name="phone_numbers"
							multi
							label="Phone Number(s)"
						/>
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save"}
							</Button>
							{onCancel ? (
								<Button onClick={onCancel} disabled={submitting}>
									Cancel
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}
